import { useEffect, useState } from "react"

import { TTimezone } from "src/data/timezones/timezones"
import {
  getOptionTimeValue,
  isTMTimePickerInputValue,
  TMTimePickerInputValue,
} from "src/ui/MTimePickerInput/timePickerUtils"
import { ErrorService } from "src/utils/ErrorService"
import { BROWSER_TIMEZONE, ZonedDateTime } from "src/utils/ZonedDateTime"

export function getTimeFromDate(
  initialDate?: Date | null
): TMTimePickerInputValue | null {
  if (!initialDate) {
    return null
  }

  const hours = initialDate.getHours().toString().padStart(2, "0")
  const minutes = initialDate.getMinutes().toString().padStart(2, "0")
  const time = `${hours}:${minutes}`

  if (!isTMTimePickerInputValue(time)) {
    ErrorService.captureMessage("Invalid time value", {
      context: { data: { initialDate, hours, minutes, time } },
    })
    return null
  }

  return time
}

/**
 * Converts a time picker input value to hours and minutes.
 *
 * @param time - The time picker input value to convert.
 * @returns The hours and minutes.
 */
export function timePickerInputValueToHoursAndMinutes(
  time: TMTimePickerInputValue
): { hours: number; minutes: number } {
  const [hours, minutes] = time.split(":")
  return { hours: Number(hours), minutes: Number(minutes) }
}

function toZonedDate(
  date: Date | null | undefined,
  timezone?: string | TTimezone
) {
  if (!date) {
    return null
  }
  return new ZonedDateTime(date, timezone ?? BROWSER_TIMEZONE).asZonedDate()
}

export function createZonedUTCDateTimeFromLocalDateAndTime(
  dateInLocalTimezone: Date | null | undefined,
  timeInLocalTimeZone: TMTimePickerInputValue | null | undefined,
  timezone?: string | TTimezone
) {
  if (!dateInLocalTimezone || !timeInLocalTimeZone) {
    return null
  }
  const { hours, minutes } =
    timePickerInputValueToHoursAndMinutes(timeInLocalTimeZone)

  const newDate = new Date(dateInLocalTimezone)
  newDate.setHours(Number(hours))
  newDate.setMinutes(Number(minutes))

  const zonedDate = ZonedDateTime.fromDateInLocalTimezone(
    newDate,
    timezone ?? BROWSER_TIMEZONE
  )

  return zonedDate.asUTCDate()
}

export function useDateTimePickerStateWithTimezone(
  onChange: (date: Date | null) => void,
  initialDateUtc: Date | null | undefined,
  timezone?: string | TTimezone
) {
  const [currentTimezone, setCurrentTimezone] = useState<string | TTimezone>(
    timezone ?? BROWSER_TIMEZONE
  )

  const [dateInLocalTime, setDateInLocalTimeState] = useState<Date | null>(
    toZonedDate(initialDateUtc, timezone)
  )
  const [timeInLocalTimezone, setTimeInLocalTimezoneState] =
    useState<TMTimePickerInputValue | null>(
      getTimeFromDate(toZonedDate(initialDateUtc, timezone))
    )

  function setDateInLocalTime(date: Date | null) {
    setDateInLocalTimeState(date)
    onChange(
      createZonedUTCDateTimeFromLocalDateAndTime(
        date,
        timeInLocalTimezone,
        timezone
      )
    )
  }

  function setTimeInLocalTimezone(time: TMTimePickerInputValue | null) {
    setTimeInLocalTimezoneState(time)
    onChange(
      createZonedUTCDateTimeFromLocalDateAndTime(
        dateInLocalTime,
        time,
        timezone
      )
    )
  }

  useEffect(() => {
    if (timezone !== currentTimezone) {
      setCurrentTimezone(timezone ?? BROWSER_TIMEZONE)
      onChange(
        createZonedUTCDateTimeFromLocalDateAndTime(
          dateInLocalTime,
          timeInLocalTimezone,
          timezone
        )
      )
    }
  }, [timezone, currentTimezone, dateInLocalTime, timeInLocalTimezone]) // eslint-disable-line react-hooks/exhaustive-deps

  return {
    dateInLocalTime,
    setDateInLocalTime,
    timeInLocalTimezone,
    setTimeInLocalTimezone,
  }
}

/**
 * Returns a function that checks if a given time is blocked based on the
 * selected date and the isDateTimeBlocked function.
 * For use in the {@link getTimePickerOptions} function.
 *
 * @param selectedDate - The date to check if the time is blocked.
 * @param isDateTimeBlocked - The function to check if the date is blocked.
 * @returns A function that checks if a given time is blocked based on the
 * selected date and the isDateTimeBlocked function.
 */
export function isTimeBlocked(
  selectedDate: Date | null,
  isDateTimeBlocked?: (date: Date) => boolean,
  timezone?: string | TTimezone
) {
  if (!isDateTimeBlocked) {
    return undefined
  }
  if (!selectedDate) {
    return undefined
  }
  return (hour: number, minute: number) => {
    const utcDate = createZonedUTCDateTimeFromLocalDateAndTime(
      selectedDate,
      getOptionTimeValue(hour, minute),
      timezone
    )

    if (!utcDate) {
      return false
    }

    return isDateTimeBlocked(utcDate)
  }
}

/**
 * Returns a function that checks if a given date is blocked based on the
 * selected time and the isDateTimeBlocked function.
 *
 * @param selectedTime - The time to check if the date is blocked.
 * @param isDateTimeBlocked - The function to check if the date is blocked.
 * @returns A function that checks if a given date is blocked based on the
 * selected time and the isDateTimeBlocked function.
 */
export function isDateBlocked(
  selectedTime: TMTimePickerInputValue | null,
  isDateTimeBlocked?: (date: Date) => boolean,
  timezone?: string | TTimezone
) {
  if (!isDateTimeBlocked) {
    return undefined
  }
  if (!selectedTime) {
    return undefined
  }
  return (date: Date) => {
    const utcDate = createZonedUTCDateTimeFromLocalDateAndTime(
      date,
      selectedTime,
      timezone
    )

    if (!utcDate) {
      return false
    }

    return isDateTimeBlocked(utcDate)
  }
}
